<template>
  <div class="p-6 bg-gray-50 min-h-screen">
    <!-- Filters -->
    <div class="flex flex-wrap justify-between items-center mb-6 gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <input v-model="search" placeholder="Search equipment..." class="p-2 border rounded-lg" />
        <input type="date" v-model="startDate" class="p-2 border rounded-lg" />
        <input type="date" v-model="endDate" class="p-2 border rounded-lg" />
        <button
          @click="applyFilters"
          class="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Apply
        </button>
      </div>
      <div class="flex items-center gap-2">
        <button
          @click="emit('openFuelPrices')"
          class="px-3.5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-1.5 text-sm font-semibold shadow-sm transition"
          title="Manage Active Fuel Market Prices"
        >
          <span>⛽</span> Fuel Prices
        </button>
        <button
          @click="emit('create')"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold shadow-sm transition"
        >
          Add Usage
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-8 text-gray-600 text-lg font-medium">Loading...</div>

    <div v-else class="overflow-x-auto bg-white shadow rounded-xl">
      <table class="min-w-full border-collapse">
        <thead class="bg-gray-100">
          <tr>
            <th class="px-4 py-3 border text-left font-semibold">Equipment</th>
            <th class="px-4 py-3 border text-left font-semibold">Hours</th>
            <th class="px-4 py-3 border text-left font-semibold">Fuel</th>
            <th class="px-4 py-3 border text-left font-semibold">Date</th>
            <th class="px-4 py-3 border text-left font-semibold">Tasks</th>
            <th class="px-4 py-3 border text-left font-semibold">Created At</th>
            <th class="px-4 py-3 border text-left font-semibold">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="paginated.length === 0">
            <td colspan="7" class="px-4 py-8 text-center text-gray-500">
              No equipment usage records found.
            </td>
          </tr>
          <tr v-for="log in paginated" :key="log.id" class="hover:bg-gray-50 transition">
            <td class="px-4 py-3 border">
              {{ log.equipment?.name || getEquipmentName(log.equipment_id) }}
            </td>
            <td class="px-4 py-3 border">{{ log.hours_used ?? '-' }}</td>
            <td class="px-4 py-3 border">
              {{ getFuelDisplay(log) }}
            </td>
            <td class="px-4 py-3 border">{{ new Date(log.log_date).toLocaleDateString() }}</td>
            <td class="px-4 py-3 border">{{ log.tasks_performed ?? '-' }}</td>
            <td class="px-4 py-3 border">
              {{ log.created_at ? new Date(log.created_at).toLocaleString() : '-' }}
            </td>
            <td class="px-4 py-3 border flex gap-3">
              <button @click="$emit('view', log.id)" class="text-blue-600 hover:underline">
                View
              </button>
              <button @click="$emit('edit', log.id)" class="text-green-600 hover:underline">
                Edit
              </button>
              <button @click="$emit('delete', log.id)" class="text-red-600 hover:underline">
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4 text-sm text-gray-700">
      <div>
        <span v-if="filtered.length > 0">
          Showing <span class="font-medium">{{ startItemIndex }}</span> to <span class="font-medium">{{ endItemIndex }}</span> of <span class="font-medium">{{ filtered.length }}</span> records
        </span>
        <span v-else>No records to display</span>
      </div>

      <div class="flex items-center gap-3">
        <!-- Items per page selector -->
        <div class="flex items-center gap-1 mr-2">
          <span class="text-gray-600">Per page:</span>
          <select v-model="perPage" @change="onPerPageChange" class="p-1 border border-gray-300 rounded text-sm bg-white">
            <option :value="10">10</option>
            <option :value="12">12</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </div>

        <!-- Pagination Buttons -->
        <div class="flex items-center gap-2">
          <button
            type="button"
            @click="prev"
            :disabled="page <= 1"
            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <span class="px-3 py-1 font-medium text-gray-700 bg-gray-100 rounded-md border border-gray-200">
            Page {{ page }} of {{ totalPages }}
          </span>

          <button
            type="button"
            @click="next"
            :disabled="page >= totalPages"
            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Next
            <svg class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useUsageStore } from '@/stores/usageStore'
import { useEquipmentStore } from '@/stores/equipmentStore'
import type { UsageLog } from '@/types/usage'
import type { Equipment } from '@/types/equipment'

const usageStore = useUsageStore()
const equipmentStore = useEquipmentStore()

const emit = defineEmits<{
  (e: 'create'): void
  (e: 'view', id: number): void
  (e: 'edit', id: number): void
  (e: 'delete', id: number): void
  (e: 'openFuelPrices'): void
}>()

const loading = ref(false)
const page = ref(1)
const perPage = ref(12)
const search = ref('')
const startDate = ref('')
const endDate = ref('')

onMounted(async () => {
  loading.value = true
  try {
    await equipmentStore.fetchEquipment()
    await usageStore.fetchUsageLogs()
  } finally {
    loading.value = false
  }
})

const logs = computed(() => usageStore.usageLogs)

const filtered = computed(() => {
  let list = Array.isArray(logs.value) ? logs.value.slice() : []

  if (search.value) {
    const q = search.value.toLowerCase()
    list = list.filter((l) => (l.equipment?.name || '').toLowerCase().includes(q))
  }

  if (startDate.value) {
    const s = new Date(startDate.value)
    list = list.filter((l) => new Date(l.log_date) >= s)
  }

  if (endDate.value) {
    const e = new Date(endDate.value)
    list = list.filter((l) => new Date(l.log_date) <= e)
  }

  // Sort descending by date (newest first), then by created_at / id
  list.sort((a, b) => {
    const timeA = a.log_date ? new Date(a.log_date).getTime() : 0
    const timeB = b.log_date ? new Date(b.log_date).getTime() : 0
    if (timeB !== timeA) return timeB - timeA
    const createdA = a.created_at ? new Date(a.created_at).getTime() : (a.id || 0)
    const createdB = b.created_at ? new Date(b.created_at).getTime() : (b.id || 0)
    return Number(createdB) - Number(createdA)
  })

  return list
})

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / perPage.value)))

const startItemIndex = computed(() => {
  if (filtered.value.length === 0) return 0
  return (page.value - 1) * perPage.value + 1
})

const endItemIndex = computed(() => {
  return Math.min(page.value * perPage.value, filtered.value.length)
})

const paginated = computed(() => {
  const start = (page.value - 1) * perPage.value
  return filtered.value.slice(start, start + perPage.value)
})

// Keep current page within valid bounds whenever totalPages changes
watch(totalPages, (newTotal) => {
  if (page.value > newTotal) {
    page.value = Math.max(1, newTotal)
  }
})

function prev() {
  if (page.value > 1) {
    page.value--
  }
}

function next() {
  if (page.value < totalPages.value) {
    page.value++
  }
}

function onPerPageChange() {
  page.value = 1
}

function applyFilters() {
  page.value = 1
}

function getEquipmentName(id: number | string | undefined) {
  if (id === undefined || id === null) return 'Unknown'

  const nid = Number(id)
  if (Number.isNaN(nid)) return 'Unknown'

  const cached = equipmentStore.nameCache?.[nid]
  if (cached) return cached

  const found = equipmentStore.equipment.find((e) => Number(e.id) === nid)
  if (found) return found.name

  equipmentStore.fetchNameIfMissing(nid).catch(() => {})

  return equipmentStore.getName(nid)
}

function getFuelDisplay(log: UsageLog) {
  if (log.fuel_used !== undefined && log.fuel_used !== null) return String(log.fuel_used)

  const eq = (log.equipment ??
    equipmentStore.equipment.find((e) => Number(e.id) === Number(log.equipment_id))) as
    | Equipment
    | undefined
  if (eq && eq.requires_fuel) return '-'

  return '—'
}
</script>
